/**
 * BookingModal.js
 * 版本：V1.05 | 更新日期：2025-07-19
 * 功能：擺攤登記模態框，提供市場選擇、重複排班、天氣預報等功能
 * 關連檔案：App.js, AddMarketForm.js, Firebase 資料庫和 Functions
 * 
 * 更新內容：
 * V1.05 (2025-07-19) - 移除區域權限提示顯示
 * - 移除預約登記介面的區域權限提示
 * - 移除管理員幫夥伴登記時的權限提示
 * - 保持權限篩選功能，但簡化介面顯示
 * 
 * V1.04 (2025-07-19) - 縣市選擇權限限制
 * - 根據夥伴區域權限篩選可選擇的縣市
 * - 自動調整縣市選擇以符合權限範圍
 * - 管理員幫夥伴登記時也限制縣市選擇
 * - 確保縣市和市場選擇的一致性
 * 
 * V1.03 (2025-07-19) - 整合區域權限系統
 * - 根據夥伴區域權限篩選可登記的市場
 * - 新增區域權限提示顯示
 * - 支援向後相容性（無權限設定的夥伴可選擇所有市場）
 * - 權限不足時的友善提示訊息
 * 
 * V1.02 (2025-07-19) - 修正表單文字顏色問題
 * - 為所有下拉選單和輸入欄位添加 text-gray-700 bg-white 樣式
 * - 解決在某些背景下文字顯示為白色而看不清楚的問題
 * - 確保表單元素在所有背景下都有良好的可讀性
 * 
 * V1.01 (2025-07-16) - 初始版本
 * - 基本擺攤登記功能
 * - 市場選擇和重複排班
 * - 天氣預報整合
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, collection, writeBatch, serverTimestamp, addDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import AddMarketForm from './AddMarketForm';
// 【AI新增】區域權限系統導入
// Why: 根據夥伴權限篩選可登記的市場
import { getRegionByCity, getRegionDisplayName, getRegionIcon } from '../../utils/regionUtils';
import { getMarketRegion } from '../../utils/marketUtils';

// 台灣城市常數
const TAIWAN_CITIES = [
    '基隆市', '臺北市', '新北市', '桃園市', '新竹市', '新竹縣', '苗栗縣', '臺中市', 
    '彰化縣', '南投縣', '雲林縣', '嘉義市', '嘉義縣', '臺南市', '高雄市', '屏東縣', 
    '宜蘭縣', '花蓮縣', '臺東縣', '澎湖縣', '金門縣', '連江縣'
];

const BookingModal = ({ config, onClose, currentUser, allBookings, markets, db, setConfirmation, appId, functions }) => {
    const { date } = config;
    
    const [selectedCity, setSelectedCity] = useState(TAIWAN_CITIES[16]);
    const [marketId, setMarketId] = useState('');
    const [remark, setRemark] = useState('');
    const [repeat, setRepeat] = useState('none');
    const [repeatCount, setRepeatCount] = useState(4);
    const [repeatEndDate, setRepeatEndDate] = useState('');
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingMarket, setIsAddingMarket] = useState(false);
    const [weatherInfo, setWeatherInfo] = useState(null);
    const [isLoadingWeather, setIsLoadingWeather] = useState(false);
    
    const bookingsColPath = `artifacts/${appId}/public/data/bookings`;
    
    // 【AI新增】根據夥伴區域權限篩選縣市和市場
    // Why: 確保夥伴只能選擇有權限的區域縣市和市場
    const filteredCities = useMemo(() => {
        // 如果夥伴有區域權限設定，則篩選縣市
        if (currentUser.regions && currentUser.regions.length > 0) {
            return TAIWAN_CITIES.filter(city => {
                const cityRegion = getRegionByCity(city);
                return currentUser.regions.includes(cityRegion);
            });
        }
        
        // 如果夥伴沒有區域權限設定，則顯示所有縣市（向後相容）
        return TAIWAN_CITIES;
    }, [currentUser.regions]);
    
    const filteredMarkets = useMemo(() => {
        // 先根據縣市篩選
        const cityFiltered = markets.filter(m => m.city === selectedCity);
        
        // 如果夥伴有區域權限設定，則進一步篩選
        if (currentUser.regions && currentUser.regions.length > 0) {
            return cityFiltered.filter(market => {
                // 取得市場的區域分類
                const marketRegion = getMarketRegion(market);
                // 檢查夥伴是否有該區域的權限
                return currentUser.regions.includes(marketRegion);
            }).sort((a, b) => a.name.localeCompare(b.name));
        }
        
        // 如果夥伴沒有區域權限設定，則顯示所有市場（向後相容）
        return cityFiltered.sort((a, b) => a.name.localeCompare(b.name));
    }, [markets, selectedCity, currentUser.regions]);
    
    // 載入天氣預報的函數
    const loadWeatherForecast = useCallback(async (city, targetDate) => {
        if (!city || !targetDate || !functions) return;
        
        setIsLoadingWeather(true);
        
        try {
            console.log('🌤️ 載入天氣預報:', { city, date: targetDate });
            
            const getWeatherForecast = httpsCallable(functions, 'getWeatherForecast');
            const result = await getWeatherForecast({ city, date: targetDate });
            
            if (result.data.success) {
                setWeatherInfo(result.data.weather);
                console.log('✅ 天氣預報載入成功:', result.data.weather);
            } else {
                setWeatherInfo(null);
                console.error('❌ 天氣預報載入失敗:', result.data.error);
            }
        } catch (err) {
            console.error('❌ 天氣預報載入錯誤:', err);
            setWeatherInfo(null);
        } finally {
            setIsLoadingWeather(false);
        }
    }, [functions]);
    
    // 【AI新增】當縣市列表改變時，確保選擇的縣市在權限範圍內
    useEffect(() => {
        if (filteredCities.length > 0 && !filteredCities.includes(selectedCity)) {
            setSelectedCity(filteredCities[0]);
        }
    }, [filteredCities, selectedCity]);
    
    useEffect(() => {
        setRemark('');
        if (!isAddingMarket && filteredMarkets.length > 0 && !marketId) {
          setMarketId(filteredMarkets[0].id);
        }
    }, [filteredMarkets, isAddingMarket, marketId]);
    
    useEffect(() => {
        if (!isAddingMarket && selectedCity && filteredMarkets.length > 0) {
            const currentMarketInCity = filteredMarkets.find(m => m.id === marketId);
            if (!currentMarketInCity) {
                setMarketId(filteredMarkets[0]?.id || '');
            }
        } else if (!isAddingMarket && selectedCity && filteredMarkets.length === 0) {
            setMarketId('');
        }
    }, [selectedCity, filteredMarkets, isAddingMarket, marketId]);
    
    useEffect(() => {
        if (selectedCity && date) {
            loadWeatherForecast(selectedCity, date);
        }
    }, [selectedCity, date, loadWeatherForecast]);
    
    useEffect(() => {
        setError('');
        setWarning('');
        if (!marketId) return;
        let isActive = true;
        const targetDate = new Date(`${date}T00:00:00`);
        const rangeStart = new Date(targetDate);
        const rangeEnd = new Date(targetDate);
        rangeStart.setDate(rangeStart.getDate() - 6);
        rangeEnd.setDate(rangeEnd.getDate() + 6);
        const toDateString = (value) => {
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const checkNearbyBookings = async () => {
            try {
                // 只讀取目標日期前後一週，避免首頁只載入本月後造成提醒漏查。
                const nearbyBookingsQuery = query(
                    collection(db, bookingsColPath),
                    where('date', '>=', toDateString(rangeStart)),
                    where('date', '<=', toDateString(rangeEnd))
                );
                const snapshot = await getDocs(nearbyBookingsQuery);
                const conflict = snapshot.docs
                    .map(item => item.data())
                    .find(booking => booking.marketId === marketId);
                if (isActive && conflict) {
                    setWarning(`提醒：${conflict.vendorName} 已在一週內登記此市場。`);
                }
            } catch (err) {
                console.error('檢查附近市場登記失敗:', err);
                // 查詢失敗時仍用已載入的本月資料提供提醒。
                const conflict = allBookings.find(booking => booking.marketId === marketId && Math.abs(targetDate.getTime() - new Date(`${booking.date}T00:00:00`).getTime()) < 7 * 24 * 60 * 60 * 1000);
                if (isActive && conflict) setWarning(`提醒：${conflict.vendorName} 已在一週內登記此市場。`);
            }
        };

        checkNearbyBookings();
        return () => { isActive = false; };
    }, [marketId, date, allBookings, db, bookingsColPath]);
    
    const handleMarketAdded = (newMarketId) => {
        setIsAddingMarket(false);
        setTimeout(() => {
            setMarketId(newMarketId);
        }, 200);
    };
    
    // 重複規則選單選項
    const getRepeatOptions = (selectedDate) => {
        const date = new Date(selectedDate);
        const weekday = date.getDay();
        const dayOfMonth = date.getDate();
        const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
        const weekdayName = weekdays[weekday];
        
        return [
            { value: 'none', label: '無重複' },
            { value: `weekly_${weekday}`, label: `每週星期${weekdayName}` },
            { value: `biweekly_${weekday}`, label: `每隔週星期${weekdayName}` },
            { value: `monthly_first_${weekday}`, label: `每月第1星期${weekdayName}` },
            { value: `monthly_${dayOfMonth}`, label: `每月${dayOfMonth}日` },
            { value: 'yearly', label: '每年' },
        ];
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!marketId) {
            return setError('請選擇一個市場！');
        }
        setIsSaving(true);
        const marketDetails = markets.find(m => m.id === marketId);
        const data = {
            marketId,
            marketName: marketDetails.name,
            marketCity: marketDetails.city,
            vendorId: currentUser.id,
            vendorName: currentUser.name,
            remark: remark.trim(),
            updatedAt: serverTimestamp(),
        };
        const batch = writeBatch(db);
        const recurringId = repeat !== 'none' ? crypto.randomUUID() : undefined;
        let bookingDates = [];
        if (repeat === 'none') {
            bookingDates = [new Date(date)];
        } else {
            bookingDates = generateRecurringDates(date, repeat);
        }
        for (let d of bookingDates) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const finalDate = `${year}-${month}-${day}`;
            const docRef = doc(collection(db, bookingsColPath));
            batch.set(docRef, { ...data, date: finalDate, createdAt: serverTimestamp(), ...(recurringId ? { recurringId } : {}) });
        }
        try {
            await batch.commit();
            onClose();
        } catch (err) {
            setError("儲存失敗：" + err.message);
        } finally {
            setIsSaving(false);
        }
    };
    
    // 產生重複日期的工具函數
    function generateRecurringDates(startDate, repeat) {
        const dates = [];
        let date = new Date(startDate);
        const endDate = new Date(`${date.getFullYear()}-12-31`);
        const targetWeekday = date.getDay();
        const targetDayOfMonth = date.getDate();
        
        while (date <= endDate) {
            if (repeat === 'none') break;
            dates.push(new Date(date));
            switch (repeat) {
                case `weekly_${targetWeekday}`:
                    date.setDate(date.getDate() + 7);
                    break;
                case `biweekly_${targetWeekday}`:
                    date.setDate(date.getDate() + 14);
                    break;
                case `monthly_first_${targetWeekday}`: {
                    let y = date.getFullYear();
                    let m = date.getMonth() + 1;
                    if (m > 11) { y++; m = 0; }
                    let d = new Date(y, m, 1);
                    while (d.getDay() !== targetWeekday) d.setDate(d.getDate() + 1);
                    date = d;
                    break;
                }
                case `monthly_${targetDayOfMonth}`: {
                    let y = date.getFullYear();
                    let m = date.getMonth() + 1;
                    if (m > 11) { y++; m = 0; }
                    date = new Date(y, m, targetDayOfMonth);
                    break;
                }
                case 'yearly': {
                    date.setFullYear(date.getFullYear() + 1);
                    break;
                }
                default:
                    break;
            }
        }
        return dates;
    }
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* 標題欄位與關閉按鈕 */}
                <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold">新增擺攤登記</h2>
                        <p className="text-lg font-semibold text-blue-600">{date}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold p-2 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="關閉"
                    >
                        ×
                    </button>
                </div>
                
                {/* 可滾動內容區域 */}
                <div className="flex-1 overflow-y-auto p-6 pt-4">
                
                {/* 天氣預報顯示區塊 */}
                {(isLoadingWeather || (weatherInfo && weatherInfo.morning) || (weatherInfo && weatherInfo.afternoon)) && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🌤️</span>
                            <h3 className="font-bold text-gray-800">{selectedCity} 天氣預報</h3>
                        </div>
                        
                        {isLoadingWeather ? (
                            <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="text-sm text-gray-600">載入天氣資料中...</span>
                            </div>
                        ) : weatherInfo && (weatherInfo.morning || weatherInfo.afternoon) ? (
                            <div className="space-y-2">
                                {/* 天氣摘要 */}
                                <div className={`p-3 rounded-lg text-sm ${
                                    weatherInfo.summary?.isBadWeather 
                                        ? 'bg-orange-100 border border-orange-300 text-orange-800' 
                                        : 'bg-green-100 border border-green-300 text-green-800'
                                }`}>
                                    <div className="flex items-center gap-2">
                                        <span>{weatherInfo.summary?.isBadWeather ? '⚠️' : '✅'}</span>
                                        <span className="font-medium">
                                            {weatherInfo.summary?.isBadWeather ? '建議評估天氣風險' : '天氣適合擺攤'}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs">{weatherInfo.summary?.description}</p>
                                </div>
                                
                                {/* 詳細天氣資訊 */}
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    {weatherInfo.morning && (
                                        <div className="bg-white p-2 rounded border">
                                            <div className="font-medium text-blue-600 mb-1">🌅 上午 (06-12時)</div>
                                            <div>天氣：{weatherInfo.morning.weather}</div>
                                            {weatherInfo.morning.maxTemp && (
                                                <div>溫度：{weatherInfo.morning.minTemp}°-{weatherInfo.morning.maxTemp}°C</div>
                                            )}
                                            <div>降雨：{weatherInfo.morning.rainProbability}%</div>
                                        </div>
                                    )}
                                    
                                    {weatherInfo.afternoon && (
                                        <div className="bg-white p-2 rounded border">
                                            <div className="font-medium text-orange-600 mb-1">☀️ 下午 (12-18時)</div>
                                            <div>天氣：{weatherInfo.afternoon.weather}</div>
                                            {weatherInfo.afternoon.maxTemp && (
                                                <div>溫度：{weatherInfo.afternoon.minTemp}°-{weatherInfo.afternoon.maxTemp}°C</div>
                                            )}
                                            <div>降雨：{weatherInfo.afternoon.rainProbability}%</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-md font-medium text-gray-700 mb-1">1. 選擇市場</label>
                        <div className="flex items-center gap-2">
                            <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className="w-1/3 p-2 border rounded-lg text-gray-700 bg-white" disabled={isAddingMarket}>
                                {filteredCities.map(city => <option key={city} value={city}>{city}</option>)}
                            </select>
                            <select value={marketId} onChange={e => setMarketId(e.target.value)} className="w-2/3 p-2 border rounded-lg text-gray-700 bg-white" disabled={isAddingMarket}>
                                <option value="">請選擇市場...</option>
                                {filteredMarkets.map(market => <option key={market.id} value={market.id}>{market.name}</option>)}
                            </select>
                        </div>

                        <div className="text-right mt-1">
                            <button type="button" onClick={() => setIsAddingMarket(!isAddingMarket)} className="text-sm text-blue-500 hover:underline">
                                {isAddingMarket ? '取消新增' : '找不到市場？點此新增'}
                            </button>
                        </div>
                    </div>
                    {isAddingMarket && <AddMarketForm selectedCity={selectedCity} db={db} onMarketAdded={handleMarketAdded} appId={appId} currentUser={currentUser} markets={markets}/>}
                    <div style={{ opacity: isAddingMarket ? 0.5 : 1 }}>
                        <label className="block text-md font-medium text-gray-700 mb-1">2. 重複排班</label>
                        <div className="space-y-2">
                            <select 
                                value={repeat} 
                                onChange={e => setRepeat(e.target.value)}
                                className="w-full p-2 border rounded-lg text-gray-700 bg-white"
                                disabled={isAddingMarket}
                            >
                                {getRepeatOptions(date).map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{ opacity: isAddingMarket ? 0.5 : 1 }}>
                        <label className="block text-md font-medium text-gray-700 mb-1">3. 備註 (選填)</label>
                        <input type="text" value={remark} onChange={e => setRemark(e.target.value)} placeholder="如有特殊事項請填寫" className="w-full p-2 border rounded-lg text-gray-700 bg-white" disabled={isAddingMarket} />
                    </div>
                    {warning && <p className="text-yellow-600 bg-yellow-100 p-3 rounded-lg text-sm">{warning}</p>}
                    {error && <p className="text-red-600 bg-red-100 p-3 rounded-lg text-sm">{error}</p>}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                        <button type="submit" disabled={isSaving || isAddingMarket} className="w-full flex-1 bg-blue-600 text-white font-bold py-3 rounded-lg disabled:bg-gray-400">
                            {isSaving ? '儲存中...' : '儲存登記'}
                        </button>
                        <button type="button" onClick={onClose} className="w-full sm:w-auto bg-gray-200 text-gray-800 font-bold py-3 px-4 rounded-lg">取消</button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );
};

export default BookingModal;
